import { NAV_PATHS } from '@modules/common/constants';
import useIsElementInScreenView from '@modules/common/hooks/useIsElementInScreenView';
import { DashboardAnalysis } from '@modules/dashboard/components/DashboardAnalysis';
import { useCellDropEventHandlers } from '@modules/dashboard/components/Layout/DashboardCell/hooks';
import { DashboardCellResizer } from '@modules/dashboard/components/Layout/DashboardCellResizer';
import { TextWidget } from '@modules/dashboard/components/TextWidget';
import {
  CELL_DRAG_IMAGE_CLASS,
  CELL_DRAG_OVERLAY_CLASS,
} from '@modules/dashboard/constants';
import { stopAutoscroll } from '@modules/dashboard/helpers';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { DragIndicator } from '@mui/icons-material';
import { Box, BoxProps, Skeleton, Stack, Tooltip } from '@mui/material';
import {
  DashboardCell as DashboardCellType,
  DashboardRow,
} from 'generated/graphql';
import { MouseEvent, useMemo, useState } from 'react';
import { DashboardMediaWidget } from '../../DashboardMediaWidget';

interface DashboardCellProps {
  id: string;
  row: DashboardRow;
  rowHeight: number;
  cell: DashboardCellType;
  sx: BoxProps['sx'];
  onMouseDown?: (event: MouseEvent<HTMLDivElement>) => void;
  isLastCell: boolean;
  showDropIndicator?: boolean;
  shouldDisableInteraction?: boolean;
}

export function DashboardCell({
  id,
  sx,
  row,
  rowHeight,
  cell,
  onMouseDown,
  isLastCell = false,
  showDropIndicator = false,
  shouldDisableInteraction = false,
}: DashboardCellProps) {
  const [shouldShowDragHandle, setShouldShowDragHandle] = useState(false);
  const [shouldEnableDrag, setShouldEnableDrag] = useState(false);
  const [isDragInProgress, setIsDragInProgress] = useState(false);
  const dispatch = useDashboardStore(({ dispatch }) => dispatch);
  const dashboard = useDashboardStore(({ dashboard }) => dashboard);

  const widgetObject = dashboard?.widgets?.find(
    (widget) => widget.id === cell.contentID,
  );

  const [cellRef, isCellInView] = useIsElementInScreenView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const { onDragOver, onDrop } = useCellDropEventHandlers({
    cellID: id,
    rowID: row.id,
  });

  /**
   * Workaround to prevent the inclusion of outer elements in the drag image
   * - when dragging an element with scrollable content.
   *
   * This issue occurs in WebKit browsers.
   */
  const addDragImage = (eve: React.DragEvent) => {
    /**
     * Element cloning technique won't work for <video> elements and other interactive elements (like <canvas>, <iframe>, etc.)
     * - are not rendered properly when used as a drag image with
     */
    if (widgetObject?.videoWidget) {
      return;
    }

    const cellDragImage = (eve.target as HTMLElement).cloneNode(
      true,
    ) as HTMLElement;
    cellDragImage.classList.add(CELL_DRAG_IMAGE_CLASS);
    cellDragImage.style.position = 'absolute';
    cellDragImage.style.top = '-1000px';
    cellDragImage.style.right = '-1000px';
    const dragOverlay = cellDragImage.querySelector(
      `.${CELL_DRAG_OVERLAY_CLASS}`,
    ) as HTMLElement;
    if (dragOverlay) {
      dragOverlay.style.display = 'block';
    }

    document.body.appendChild(cellDragImage);
    eve.dataTransfer.setDragImage(cellDragImage, 0, 0);
  };

  const removeDragImage = () => {
    const cellDragImages = document.getElementsByClassName(
      CELL_DRAG_IMAGE_CLASS,
    );

    [...cellDragImages].forEach((cellDragImage) => {
      cellDragImage?.parentElement?.removeChild(cellDragImage);
    });
  };

  function onDragStart(eve: React.DragEvent) {
    setIsDragInProgress(true);
    eve.dataTransfer.effectAllowed = 'move';
    eve.dataTransfer.setData(
      'application/json',
      JSON.stringify({ cellID: id, rowID: row.id }),
    );

    addDragImage(eve);

    dispatch({
      type: DashboardActionType.SET_VISUAL,
      payload: {
        dragItemData: {
          cellID: id,
          rowID: row.id,
        },
      },
    });
  }

  function onDragEnd(eve: React.DragEvent) {
    eve.preventDefault();
    setIsDragInProgress(false);

    removeDragImage();

    stopAutoscroll();

    setTimeout(() => {
      dispatch({
        type: DashboardActionType.SET_VISUAL,
        payload: {
          dragItemData: null,
          dropTargetData: null,
        },
      });
    }, 100);
  }

  function onDragLeave() {
    setTimeout(() => {
      dispatch({
        type: DashboardActionType.SET_VISUAL,
        payload: {
          dropTargetData: null,
        },
      });
    }, 100);
  }

  const isReportRoute = window.location.pathname.includes(
    `/${NAV_PATHS.REPORT}/`,
  );

  const canRenderCellContent = isReportRoute || isCellInView;

  // Memo is required to improve the scroll experience (kills rerender on scroll)
  const cellContent = useMemo(() => {
    const enableDrag = shouldDisableInteraction
      ? undefined
      : () => {
          setShouldEnableDrag(true);
          setShouldShowDragHandle(true);
        };

    const disableDrag = shouldDisableInteraction
      ? undefined
      : () => setShouldEnableDrag(false);

    if (!canRenderCellContent) {
      return <Skeleton variant="rounded" width="100%" height="100%" />;
    }

    if (widgetObject?.analysis) {
      return (
        <DashboardAnalysis
          rowID={row.id}
          analysis={widgetObject.analysis}
          shouldDisableInteraction={false}
          onHeaderMouseEnter={enableDrag}
          onHeaderMouseLeave={() => {
            disableDrag?.();
            // Drag handle should not be present when the header is out of focus
            setShouldShowDragHandle(false);
          }}
        />
      );
    }

    if (widgetObject?.textWidget) {
      return (
        <TextWidget
          rowID={row.id}
          cell={cell}
          content={widgetObject.textWidget.text}
          onFocus={() => {
            disableDrag?.();
            // Drag handle should be present when the text editor is focused
            setShouldShowDragHandle(true);
          }}
          onBlur={() => {
            setShouldShowDragHandle(false);
          }}
        />
      );
    }

    if (widgetObject?.imageWidget || widgetObject?.videoWidget) {
      return (
        <DashboardMediaWidget
          rowID={row.id}
          cell={cell}
          imageWidget={widgetObject.imageWidget}
          videoWidget={widgetObject.videoWidget}
        />
      );
    }

    return <Skeleton variant="rounded" width="100%" height="100%" />;
  }, [
    widgetObject,
    row.id,
    cell,
    canRenderCellContent,
    shouldDisableInteraction,
  ]);

  const isTextContent = !!widgetObject?.textWidget;
  const dragHandle = shouldDisableInteraction ? null : (
    <Tooltip arrow title="Drag to Move" placement="top">
      <Box
        className="e-drag-handle"
        sx={{
          cursor: 'grab',
          position: 'absolute',
          left: isTextContent ? '-2px' : 2,
          top: isTextContent ? 4 : 12,
          zIndex: (theme) => theme.zIndex.drawer - 1,
          opacity: shouldShowDragHandle || shouldEnableDrag ? 1 : 0,
          paddingTop: isTextContent ? '4px' : undefined,
          transition: 'opacity .25s ease',
          background: (theme) => theme.palette.background.paper,
        }}
        onMouseEnter={() => setShouldEnableDrag(true)}
        onMouseLeave={() => setShouldEnableDrag(false)}
      >
        <DragIndicator sx={{ fontSize: '14px' }} />
      </Box>
    </Tooltip>
  );

  const dragIndicator = shouldDisableInteraction ? null : (
    <Box
      className={CELL_DRAG_OVERLAY_CLASS}
      sx={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        display: isDragInProgress ? 'block' : 'none',
        background: 'grey',
        opacity: 0.5,
      }}
    />
  );

  return (
    <Stack
      className="cell-container"
      direction="row"
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
    >
      <Box
        className="cell"
        id={id}
        ref={cellRef}
        sx={{
          ...sx,
          height: rowHeight,
          position: 'relative',
          cursor: shouldEnableDrag ? 'grab' : undefined,
          '&:hover': {
            '.e-drag-handle': {
              opacity: 1,
            },
          },
        }}
        draggable={shouldEnableDrag}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        {dragHandle}
        {dragIndicator}
        {cellContent}
      </Box>
      <DashboardCellResizer
        rowID={row.id}
        rowHeight={rowHeight}
        onMouseDown={onMouseDown}
        skipInteractions={isLastCell || shouldDisableInteraction}
        showDropIndicator={showDropIndicator}
        sx={{
          position: isLastCell ? 'absolute' : 'static',
          right: isLastCell ? '-10px' : '',
        }}
      />
    </Stack>
  );
}
